import { Layout } from '@/components/Layout';
import { ProgressPhaseCard } from '@/components/Cards';
import { progressPhases, updateLogs } from '@/data/index';
import { IMAGES } from '@/assets/images';
import { motion } from 'framer-motion';
import { Calendar, CheckCircle2, Clock, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';

export default function ProgressPage() {
  const overallCompletion = 35;

  const milestones = [
    { label: 'Nhận hồ sơ đăng ký', date: 'QI/2026', status: 'upcoming' },
    { label: 'Cất nóc / Hoàn thiện', date: 'QI/2028', status: 'upcoming' },
    { label: 'Bàn giao căn hộ', date: 'QIII/2028', status: 'upcoming' },
  ];

  const constructionPhotos = [
    { src: IMAGES.HARMONY_RENDER_MAIN, alt: 'Phối cảnh HARMONY Xuân Phương – Tổng thể' },
    { src: IMAGES.HARMONY_RENDER_3D, alt: 'Phối cảnh 3D HARMONY Xuân Phương' },
    { src: IMAGES.HARMONY_RENDER_ANGLE, alt: 'Phối cảnh HARMONY Xuân Phương – Góc khác' },
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="container mx-auto px-4 py-12 max-w-7xl"
        >
          <div className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
              Tiến Độ Xây Dựng
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Theo dõi tiến độ thi công dự án HARMONY Xuân Phương – OCT1 &amp; OCT3
            </p>
          </div>

          <Card className="mb-12 border-primary/20 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <TrendingUp className="w-6 h-6 text-primary" />
                Tổng Quan Tiến Độ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-lg font-semibold text-foreground">
                      Tiến độ hoàn thành tổng thể
                    </span>
                    <span className="text-3xl font-bold text-primary font-mono">
                      {overallCompletion}%
                    </span>
                  </div>
                  <Progress value={overallCompletion} className="h-4" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
                  {milestones.map((milestone, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center gap-3 p-4 rounded-lg bg-accent/10 border border-accent/20"
                    >
                      <Calendar className="w-5 h-5 text-primary" />
                      <div>
                        <p className="font-semibold text-foreground">
                          {milestone.label}
                        </p>
                        <p className="text-sm text-muted-foreground font-mono">
                          {milestone.date}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-foreground flex items-center gap-2">
              <Clock className="w-7 h-7 text-primary" />
              Các Giai Đoạn Thi Công
            </h2>
            <div className="relative">
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border hidden md:block" />
              <div className="space-y-6">
                {progressPhases.map((phase, index) => (
                  <motion.div
                    key={phase.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="relative"
                  >
                    <div className="hidden md:block absolute left-8 top-6 w-4 h-4 rounded-full bg-primary border-4 border-background transform -translate-x-1/2" />
                    <div className="md:ml-16">
                      <ProgressPhaseCard phase={phase} />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-foreground">
              Hình Ảnh Tiến Độ Thi Công
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {constructionPhotos.map((photo, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="group relative overflow-hidden rounded-xl shadow-lg aspect-[4/3]"
                >
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </motion.div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  Nhật Ký Cập Nhật
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {updateLogs.map((log, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex gap-4 pb-4 border-b border-border last:border-0"
                    >
                      <div className="flex-shrink-0">
                        <Badge variant="outline" className="font-mono text-xs">
                          {log.date}
                        </Badge>
                      </div>
                      <p className="text-sm text-foreground">{log.description}</p>
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Các Mốc Quan Trọng
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                      <div>
                        <p className="font-semibold text-foreground">Mở bán đợt 1</p>
                        <p className="text-sm text-muted-foreground font-mono">
                          Q3/2026
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Chính thức mở bán 200 căn hộ tòa CT1 cho các đối tượng đủ điều kiện
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                      <div>
                        <p className="font-semibold text-foreground">Cất nóc tòa CT1</p>
                        <p className="text-sm text-muted-foreground font-mono">
                          Q4/2026
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Hoàn thành thi công kết cấu chính tòa CT1, bắt đầu giai đoạn hoàn thiện
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                      <div>
                        <p className="font-semibold text-foreground">Bàn giao căn hộ</p>
                        <p className="text-sm text-muted-foreground font-mono">
                          Q4/2027
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Hoàn thành toàn bộ dự án, bàn giao căn hộ cho cư dân và làm thủ tục sổ hồng
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border">
                    <p className="text-sm text-muted-foreground">
                      <span className="font-semibold text-foreground">Lưu ý:</span> Tiến độ có thể thay đổi tùy thuộc vào điều kiện thời tiết và các yếu tố khách quan. Mọi cập nhật sẽ được thông báo kịp thời trên website.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
}